import { Get_Last_Coinflip_GamesQuery, Get_Last_Coinflip_Games_By_PlayerQuery } from '@/__generated__/graphql'
import { GET_COINFLIP_GAMES, GET_COINFLIP_GAMES_BY_PLAYER } from '@/api/query'
import { LastGamesList } from '@/components/organisms/LastGamesList/LastGamesList'
import { Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { useCoinflipStore } from './CoinflipProvider'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { useSession } from '@/context/SessionProvider'

enum Tab {
  RECENT,
  LUCKIEST,
}

export const LastGames = memo(() => {
  const { t } = useTranslation()
  const history = useCoinflipStore((state) => state.view.history)
  const [tab, setTab] = useState(Tab.RECENT)
  const [onlyMine, setOnlyMine] = useState(false)
  const modal = useAppModal()
  const { account } = useSession()
  const address = account?.injectiveAddress

  const allQuery = useQuery(GET_COINFLIP_GAMES, {
    pollInterval: 30_000,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
    skip: onlyMine,
  })

  const myQuery = useQuery(GET_COINFLIP_GAMES_BY_PLAYER, {
    pollInterval: 30_000,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
    variables: { player: address || '' },
    skip: !onlyMine || !address,
  })

  useEffect(() => {
    if (onlyMine) myQuery.refetch()
    else allQuery.refetch()
  }, [history])

  const allGames = transformCoinflipGames(allQuery.data)
  const myGames = transformCoinflipGamesByPlayer(myQuery.data)

  const activeGames = onlyMine ? myGames : tab === Tab.RECENT ? allGames.recent : allGames.lucky
  const activeLoading = onlyMine ? myQuery.loading : allQuery.loading
  const activeError = onlyMine ? !!myQuery.error : !!allQuery.error

  return (
    <Flex h={'full'} p={{ base: 4, sm: 5 }} bg={'ninja.600'} rounded={'2xl'} flexDir={'column'}>
      <Flex flexShrink={0} gap={2} mb={3} alignItems={'center'}>
        <SimpleGrid
          flex={1}
          gap={1}
          p={1}
          h={10}
          fontSize={'xs'}
          fontWeight={700}
          bg={'ninja.500'}
          rounded={'xl'}
          columns={2}
        >
          <Flex
            onClick={() => { setTab(Tab.RECENT); setOnlyMine(false) }}
            cursor={'pointer'}
            rounded={'lg'}
            bg={tab === Tab.RECENT && !onlyMine ? '#3D3C70' : 'transparent'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text color={tab === Tab.RECENT && !onlyMine ? 'white' : 'ninja.300'}>{t('common.last-games')}</Text>
          </Flex>
          <Flex
            onClick={() => { setTab(Tab.LUCKIEST); setOnlyMine(false) }}
            bg={tab === Tab.LUCKIEST && !onlyMine ? '#3D3C70' : 'transparent'}
            cursor={'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text color={tab === Tab.LUCKIEST && !onlyMine ? 'white' : 'ninja.300'}>{t('common.lucky-bets')}</Text>
          </Flex>
        </SimpleGrid>
        {address && (
          <Flex
            onClick={() => setOnlyMine(!onlyMine)}
            cursor={'pointer'}
            h={10}
            px={3}
            flexShrink={0}
            rounded={'xl'}
            bg={onlyMine ? '#3D3C70' : 'ninja.500'}
            alignItems={'center'}
            justifyContent={'center'}
            transition={'0.15s'}
            border={'1px solid'}
            borderColor={onlyMine ? '#6B69C4' : 'transparent'}
            _hover={{ bg: onlyMine ? '#4a4890' : 'ninja.400' }}
          >
            <Text fontSize={'xs'} fontWeight={700} color={onlyMine ? 'white' : 'ninja.300'}>
              {t('common.my-games')}
            </Text>
          </Flex>
        )}
      </Flex>
      <LastGamesList
        gameDetailsCallback={(id) => modal.openModalNew(AppModals.CoinflipGameDetails, { gameId: id })}
        error={activeError}
        loading={activeLoading}
        games={activeGames}
        viewAllHref={onlyMine && address ? `/activity/${address}?game=coinflip` : undefined}
      />
    </Flex>
  )
})

const mapCoinflipGame = (game: any) => ({
  id: (game?.id || '') as string,
  player: game?.player || '',
  status: game?.status || '',
  payout: { amount: game?.payable || '', denom: game?.denom || '' },
  bet: { amount: game?.bet || '', denom: game?.denom || '' },
  betUsd: game?.betUsd != null ? String(game.betUsd) : undefined,
  payoutUsd: game?.payoutUsd != null ? String(game.payoutUsd) : undefined,
})

const transformCoinflipGames = (data?: Get_Last_Coinflip_GamesQuery) => {
  return {
    recent: data?.coinflipGames?.nodes.map(mapCoinflipGame).reverse() || [],
    lucky: data?.appCoinflipLeaderboardLuckyBets?.nodes.map(mapCoinflipGame).reverse() || [],
  }
}

const transformCoinflipGamesByPlayer = (data?: Get_Last_Coinflip_Games_By_PlayerQuery) => {
  return data?.coinflipGames?.nodes.map(mapCoinflipGame).reverse() || []
}
